import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";

export const DEFAULT_QUERY_STALE_TIME = 60 * 60 * 1000; // 1 hora

const EMPTY_ARRAY = [];

// El complemento de mutaciones (create/update/delete) se diseñaría como useServerMutation en un archivo hermano, reutilizando queryKeyBase.
export const useServerPagedQuery = ({
  queryKeyBase,
  queryFn,
  filters = {},
  initialPage = 1,
  initialRowsPerPage = 10,
  enabled = true,
  staleTime = DEFAULT_QUERY_STALE_TIME,
  getItems = (raw) => raw?.items ?? EMPTY_ARRAY,
  getTotal = (raw) => raw?.total ?? 0,
  ...queryOptions
}) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [extraFilters, setExtraFilters] = useState(filters);

  const variables = useMemo(
    () => ({
      ...extraFilters,
      page,
      pageSize: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
      limit: rowsPerPage,
    }),
    [extraFilters, page, rowsPerPage]
  );

  const queryKey = useMemo(() => [...queryKeyBase, variables], [queryKeyBase, variables]);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => queryFn(variables, { signal }),
    enabled,
    staleTime,
    placeholderData: keepPreviousData,
    ...queryOptions,
  });

  const onChangePage = useCallback((newPage) => setPage(newPage), []);

  const onChangeRowsPerPage = useCallback((newRowsPerPage, newPage = 1) => {
    setRowsPerPage(newRowsPerPage);
    setPage(newPage);
  }, []);

  const setFilters = useCallback((patch, { resetPage = true } = {}) => {
    setExtraFilters((prev) => ({ ...prev, ...patch }));
    if (resetPage) setPage(1);
  }, []);

  const replaceFilters = useCallback((newFilters, { resetPage = true } = {}) => {
    setExtraFilters(newFilters);
    if (resetPage) setPage(1);
  }, []);

  const refresh = useCallback(() => query.refetch(), [query.refetch]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeyBase }),
    [queryClient, queryKeyBase]
  );

  const patchQueryData = useCallback(
    (updater) => queryClient.setQueryData(queryKey, updater),
    [queryClient, queryKey]
  );

  const data = useMemo(
    () => getItems(query.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data]
  );

  const total = useMemo(
    () => getTotal(query.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data]
  );

  return {
    data,
    total,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isError: query.isError,
    page,
    rowsPerPage,
    filters: extraFilters,
    onChangePage,
    onChangeRowsPerPage,
    setFilters,
    replaceFilters,
    refresh,
    invalidate,
    queryKey,
    queryKeyBase,
    patchQueryData,
  };
};
